'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../lib/cn';

const containerVariants = cva('mx-auto w-full px-4 sm:px-6 lg:px-8', {
  variants: {
    size: {
      sm: 'max-w-3xl',
      md: 'max-w-5xl',
      lg: 'max-w-6xl',
      xl: 'max-w-7xl',
      full: 'max-w-none',
    },
  },
  defaultVariants: { size: 'lg' },
});

export interface ContainerProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof containerVariants> {}

export const Container = React.forwardRef<HTMLDivElement, ContainerProps>(
  ({ className, size, ...props }, ref) => (
    <div ref={ref} className={cn(containerVariants({ size }), className)} {...props} />
  ),
);
Container.displayName = 'Container';

type Gap = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12;

const gapClass: Record<Gap, string> = {
  0: 'gap-0',
  1: 'gap-1',
  2: 'gap-2',
  3: 'gap-3',
  4: 'gap-4',
  5: 'gap-5',
  6: 'gap-6',
  8: 'gap-8',
  10: 'gap-10',
  12: 'gap-12',
};

export interface StackProps extends React.HTMLAttributes<HTMLDivElement> {
  gap?: Gap;
  as?: keyof React.JSX.IntrinsicElements;
  align?: 'start' | 'center' | 'end' | 'stretch';
  justify?: 'start' | 'center' | 'end' | 'between' | 'around';
}

const alignClass: Record<NonNullable<StackProps['align']>, string> = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  stretch: 'items-stretch',
};

const justifyClass: Record<NonNullable<StackProps['justify']>, string> = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
  between: 'justify-between',
  around: 'justify-around',
};

export const Stack = React.forwardRef<HTMLDivElement, StackProps>(
  ({ className, gap = 4, as = 'div', align, justify, ...props }, ref) => {
    const Comp = as as React.ElementType;
    return (
      <Comp
        ref={ref}
        className={cn(
          'flex flex-col',
          gapClass[gap],
          align && alignClass[align],
          justify && justifyClass[justify],
          className,
        )}
        {...props}
      />
    );
  },
);
Stack.displayName = 'Stack';

export interface InlineProps extends React.HTMLAttributes<HTMLDivElement> {
  gap?: Gap;
  as?: keyof React.JSX.IntrinsicElements;
  wrap?: boolean;
  align?: 'start' | 'center' | 'end' | 'baseline' | 'stretch';
  justify?: 'start' | 'center' | 'end' | 'between' | 'around';
}

const inlineAlignClass: Record<NonNullable<InlineProps['align']>, string> = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  baseline: 'items-baseline',
  stretch: 'items-stretch',
};

export const Inline = React.forwardRef<HTMLDivElement, InlineProps>(
  ({ className, gap = 4, as = 'div', wrap = false, align = 'center', justify, ...props }, ref) => {
    const Comp = as as React.ElementType;
    return (
      <Comp
        ref={ref}
        className={cn(
          'flex flex-row',
          gapClass[gap],
          wrap && 'flex-wrap',
          inlineAlignClass[align],
          justify && justifyClass[justify],
          className,
        )}
        {...props}
      />
    );
  },
);
Inline.displayName = 'Inline';

export interface SpacerProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: 1 | 2 | 3 | 4 | 6 | 8 | 12 | 16;
  axis?: 'horizontal' | 'vertical';
}

const spacerSize: Record<NonNullable<SpacerProps['size']>, { v: string; h: string }> = {
  1: { v: 'h-1', h: 'w-1' },
  2: { v: 'h-2', h: 'w-2' },
  3: { v: 'h-3', h: 'w-3' },
  4: { v: 'h-4', h: 'w-4' },
  6: { v: 'h-6', h: 'w-6' },
  8: { v: 'h-8', h: 'w-8' },
  12: { v: 'h-12', h: 'w-12' },
  16: { v: 'h-16', h: 'w-16' },
};

export const Spacer = React.forwardRef<HTMLDivElement, SpacerProps>(
  ({ className, size = 4, axis = 'vertical', ...props }, ref) => {
    const sizing = spacerSize[size];
    return (
      <div
        ref={ref}
        aria-hidden="true"
        className={cn(axis === 'vertical' ? sizing.v : sizing.h, className)}
        {...props}
      />
    );
  },
);
Spacer.displayName = 'Spacer';
